import { useEffect, useState } from "react";
import { getAttorneyInbox } from "../api/attorneyInbox";
import { viewAttorneyDocument } from "../api/documents";
import AttorneyNav from "../components/AttorneyNav";
import StatusMessage from "../components/StatusMessage";
import { getErrorMessage } from "../utils/apiErrors";

export default function AttorneyInbox() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingId, setViewingId] = useState(null);
  const [error, setError] = useState("");

  const loadDocuments = async () => {
    setLoading(true);

    try {
      const res = await getAttorneyInbox();
      setDocuments(res.data.data || []);
    } catch (err) {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const openDocument = async (document) => {
    if (viewingId) return;
    setViewingId(document.id);
    setError("");
    try {
        const response = await viewAttorneyDocument(document.id);
        const blob = new Blob([response.data], { type: "application/pdf" });
        const objectUrl = URL.createObjectURL(blob);

        window.open(objectUrl, "_blank", "noopener,noreferrer");

        setTimeout(() => {
          URL.revokeObjectURL(objectUrl);
        }, 60000);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to open this document securely."));
    } finally {
      setViewingId(null);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div className="brand-mark">CV</div>

          <div>
            <h5 className="mb-0">CounselVault</h5>
            <div className="small opacity-75">Attorney Inbox</div>
          </div>
        </div>

        <AttorneyNav />
      </div>

      <main className="page-wrap">
        <StatusMessage type="warning">{error}</StatusMessage>
        <div className="mb-4">
          <h2 className="mb-1">Inbox</h2>
          <p className="text-muted mb-0">
            Documents received from approved inmates.
          </p>
        </div>

        <div className="portal-card">
          <div className="portal-card-header d-flex justify-content-between align-items-center">
            <div>
              <h5 className="mb-0">Received from Inmates</h5>
              <div className="text-muted small">
                Privileged legal documents sent to you by approved inmate/client relationships.
              </div>
            </div>

            <button className="btn btn-sm btn-outline-secondary" onClick={loadDocuments}>
              Refresh
            </button>
          </div>

          <div className="portal-card-body">
            {loading ? (
              <div className="text-muted">Loading inbox...</div>
            ) : documents.length === 0 ? (
              <div className="text-center py-5">
                <h5>No received documents yet</h5>
                <p className="text-muted mb-0">
                  Documents sent by approved inmates will appear here.
                </p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Facility</th>
                      <th>Inmate</th>
                      <th>Status</th>
                      <th>Received</th>
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id}>
                        <td>
                          <strong>{doc.title}</strong>
                          <div className="text-muted small">
                            {doc.original_filename || "PDF document"}
                          </div>
                        </td>

                        <td>{doc.facility?.name || "Facility"}</td>

                        <td>
                          {doc.inmate ? (
                            <>
                              <strong>
                                {doc.inmate.first_name} {doc.inmate.last_name}
                              </strong>
                              <div className="text-muted small">
                                #{doc.inmate.inmate_number}
                              </div>
                            </>
                          ) : (
                            "Inmate"
                          )}
                        </td>

                        <td>
                          <span className="status-pill status-released">
                            Received
                          </span>
                        </td>

                        <td>{new Date(doc.created_at).toLocaleString()}</td>

                        <td className="text-end">
                          <button
                            className="btn btn-sm btn-outline-dark"
                            onClick={() => openDocument(doc)}
                            disabled={viewingId !== null}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
